import { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Spinner,
  Text,
  toastManager,
} from "@salestrak/ui";

import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

type DeleteStageDialogProps = Omittable<{
  stageId: string | null;
  stageName: string;
  issueCount?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
  onManageWorkflow?: () => void;
}>;

export function DeleteStageDialog({
  stageId,
  stageName,
  issueCount = 0,
  open,
  onOpenChange,
  onDeleted,
  onManageWorkflow,
}: DeleteStageDialogProps) {
  const { deleteStage, validateStageDeletion } = useStageMutations();

  const [validating, setValidating] = useState(false);
  const [canDelete, setCanDelete] = useState<boolean | null>(null);
  const [blockerMessage, setBlockerMessage] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !stageId) {
      setCanDelete(null);
      setBlockerMessage("");
      return;
    }

    if (issueCount > 0) {
      setCanDelete(false);
      return;
    }

    let cancelled = false;
    setValidating(true);

    validateStageDeletion(stageId)
      .then((result) => {
        if (cancelled) return;
        setCanDelete(result.canDelete);
        if (!result.canDelete && result.blockers?.length) {
          const messages = result.blockers.map((b: unknown) =>
            typeof b === "string"
              ? b
              : ((b as { message?: string }).message ?? JSON.stringify(b)),
          );
          setBlockerMessage(messages.join(". "));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCanDelete(false);
        setBlockerMessage("Failed to validate. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, stageId, issueCount, validateStageDeletion]);

  const handleDelete = useCallback(async () => {
    if (!stageId) return;
    setDeleting(true);
    try {
      await deleteStage(stageId);
      toastManager.add({ title: "Stage deleted", type: "success" });
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toastMutationError(err, "Failed to delete stage");
    } finally {
      setDeleting(false);
    }
  }, [stageId, deleteStage, onOpenChange, onDeleted]);

  const handleManageWorkflow = useCallback(() => {
    onOpenChange(false);
    onManageWorkflow?.();
  }, [onOpenChange, onManageWorkflow]);

  if (!stageId) return null;

  const hasIssues = issueCount > 0;
  const issueLabel = issueCount === 1 ? "1 project" : `${issueCount} projects`;

  if (validating) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{stageName}&rdquo;?</AlertDialogTitle>
          </AlertDialogHeader>
          <Box padding="lg" align="center">
            <Spinner />
          </Box>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    );
  }

  if (canDelete === false) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot delete stage</AlertDialogTitle>
            <AlertDialogDescription>
              {hasIssues ? (
                <Text size="sm">
                  <Text size="sm" weight="semibold">{stageName}</Text>
                  {" "}cannot be deleted until all{" "}
                  <Text size="sm" weight="semibold">{issueLabel}</Text>
                  {" "}are moved to a different stage, workflow or deleted.
                </Text>
              ) : (
                blockerMessage || "This stage cannot be deleted."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            {hasIssues && onManageWorkflow ? (
              <Button onClick={handleManageWorkflow}>Manage workflow</Button>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete &ldquo;{stageName}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this stage?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
            loading={deleting}
          >
            Delete stage
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
